import _remove from 'lodash/remove';
import _indexOf from 'lodash/indexOf';
import _find from 'lodash/find';
import get from 'lodash/get';

/**
 * @desc  GroupService class.
 * @ngInject
 */
export default class StudentService {
  /**
   * Constructor of the class.
   *
   * @param {$rootScope} $rootScope
   * @param {$q} $q
   * @param {$log}  $log
   * @param {$http} $http
   */
  constructor(
    $rootScope, $q, $log, $http
  ) {
    this.$rootScope = $rootScope;
    this.$q = $q;
    this.$log = $log;
    this.$http = $http;

    this.students = [];
    this.unregisterStudents = [];
    this.waitingStudents = [];
    this.unlistedStudent = [];
    this.attendanceRegistrations = [];
    this.allAttendanceRegistrations = [];
    this.studentAttendances = [];
    this.groupsRegisterRecords = [];

    this.$rootScope.$on('scr.select', () => {
      this.unregisterStudents.splice(0, this.unregisterStudents.length);
      this.waitingStudents.splice(0, this.waitingStudents.length);
      this.attendanceRegistrations.splice(0, this.attendanceRegistrations.length);
      this.studentAttendances.splice(0, this.studentAttendances.length);
      this.groupsRegisterRecords.splice(0, this.groupsRegisterRecords.length);
    });
  }

  getAll(params = {}) {
    return this.$http({
      method: 'GET',
      url: `/SCR/schoolCommonRoom/${this.$rootScope.me.schoolCommonRoomId}/student`,
      params: {
        limit: params.limit,
        offset: params.offset,
        condition: params.query
      }
    }).then(({data}) => {
      const {students, total} = data;
      this.students = students;
      return {
        data: {
          total,
          students: students.map(item => ({
            ...item,
            isDisabled: get(item, 'disabled', false),
            groupId: Number(get(item, 'groupId', -1))
          }))
        }
      };
    }, err => this.$q.reject(err));
  }

  getForRegistration(options = {}) {
    return this.$http({
      method: 'GET',
      url: `/SCR/schoolCommonRoom/${this.$rootScope.me.schoolCommonRoomId}/studentsForRegistration`,
      params: {
        groupId: options.groupId,
        limit: options.limit,
        offset: options.offset,
        condition: options.query,
        tagId: options.tagId,
        studentId: options.studentId
      },
      ignoreLoadingBar: !options.showLoaderSpinner
    }).then(result => {
      this.unregisterStudents = result.data.students;
      return result;
    }, err => this.$q.reject(err));
  }

  getUnlistedStudents(options = {}) {
    return this.$http({
      method: 'GET',
      url: `/SCR/schoolCommonRoom/${this.$rootScope.me.schoolCommonRoomId}/schoolClass/${options.data}/unlistedStudent`
    }).then(result => {
      this.unlistedStudents = result.data.students;
      return this.unlistedStudents;
    }, err => this.$q.reject(err));
  }

  getWaitingStudents(options = {}) {
    return this.$http({
      method: 'GET',
      url: `/SCR/employee/${this.$rootScope.me.employeeId}/waitingStudents`,
      params: {groupId: options.groupId},
      ignoreLoadingBar: !options.showLoaderSpinner
    }).then(result => {
      this.waitingStudents = result.data.waitingStudents;
      return this.waitingStudents;
    }, err => this.$q.reject(err));
  }

  getCreatedWaitingStudents(options = {}) {
    return this.$http({
      method: 'GET',
      url: `/SCR/employee/${this.$rootScope.me.employeeId}/createdWaitingStudents`,
      params: {groupId: options.groupId},
      ignoreLoadingBar: !options.showLoaderSpinner
    }).then(result => {
      this.createdWaitingStudents = result.data.waitingStudents;
      return this.createdWaitingStudents;
    }, err => this.$q.reject(err));
  }

  addAttendance(attendance) {
    const idsToDelete = new Set(attendance.studentsId);
    return this.$http({
      method: 'POST',
      url: `/SCR/schoolCommonRoom/${this.$rootScope.me.schoolCommonRoomId}/students/attendance`,
      data: {attendance}
    }).then(result => {
      if (this.unregisterStudents.length > 0) {
        _remove(this.unregisterStudents, item => idsToDelete.has(Number(item.id)));
      }

      if (this.waitingStudents.length > 0) {
        _remove(this.waitingStudents, item => idsToDelete.has(Number(item.id)));
      }

      return result;
    }, err => this.$q.reject(err));
  }

  editAttendance(attendance) {
    return this.$http({
      method: 'PUT',
      url: `/SCR/student/${attendance.student.id}/students/attendance/${attendance.id}`,
      data: {attendance}
    }).then(result => result, err => this.$q.reject(err));
  }

  editAttendanceRegistration(attendance) {
    return this.$http({
      method: 'PUT',
      url: `/SCR/student/${attendance.student.id}/attendanceRegistration/${attendance.id}`,
      data: {attendance}
    }).then(result => result, err => this.$q.reject(err));
  }

  closeAttendance(attendance) {
    const idsToDelete = new Set(attendance.attendancesId);
    return this.$http({
      method: 'PUT',
      url: `/SCR/schoolCommonRoom/${this.$rootScope.me.schoolCommonRoomId}/students/attendanceDeregistration`,
      data: {attendance}
    }).then(result => {
      if (this.attendanceRegistrations.length > 0) {
        _remove(this.attendanceRegistrations, item => idsToDelete.has(Number(item.id)));
      }

      return result;
    }, err => this.$q.reject(err));
  }

  deleteAttendance(studentId, id) {
    return this.$http({
      method: 'DELETE',
      url: `/SCR/student/${studentId}/attendance/${id}`
    }).then(result => {
      if (this.attendanceRegistrations.length > 0) {
        _remove(this.attendanceRegistrations, item => item.id === id);
      }

      return result;
    }, err => this.$q.reject(err));
  }

  getAttendances(options = {}) {
    return this.$http({
      method: 'GET',
      url: `/SCR/employee/${this.$rootScope.me.employeeId}/attendance`,
      params: {
        groupId: options.groupId,
        classRoomId: options.classRoomId,
        day: options.day,
        invalid: options.invalid
      },
      ignoreLoadingBar: !options.showLoaderSpinner
    }).then(result => {
      this.attendanceRegistrations = result.data.attendances;
      return this.attendanceRegistrations;
    }, err => this.$q.reject(err));
  }

  getAttendancesByStudent(studentId) {
    return this.$http({
      method: 'GET',
      url: `/SCR/student/${studentId}/attendance`
    }).then(result => {
      this.studentAttendances = result.data.attendances;
      if (this.studentAttendances.length > 0) {
        this.studentAttendances.forEach(attendance => {
          attendance.allDay = false;
          if (!attendance.invalid) {
            attendance.color = '#8BC34A';
          }

          attendance.start = attendance.date + ' ' + attendance.startTime;
          if (attendance.endTime) {
            attendance.end = attendance.date + ' ' + attendance.endTime;
            attendance.title = attendance.startTime.slice(0, -3) + ' - ' + attendance.endTime.slice(0, -3);
          } else {
            attendance.allDay = true;
            attendance.color = '#FFEB3B';
            attendance.title = attendance.startTime.slice(0, -3) + ' - ...';
            attendance.textColor = '#212121';
          }
        });
      }

      return this.studentAttendances;
    }, err => this.$q.reject(err));
  }

  getAllAttendanceRegistrations(options = {}) {
    return this.$http({
      method: 'GET',
      url: `/SCR/schoolCommonRoom/${this.$rootScope.me.schoolCommonRoomId}/attendances/registrations`,
      params: {
        groupId: options.groupId,
        day: options.day
      },
      ignoreLoadingBar: !options.showLoaderSpinner
    }).then(result => {
      this.allAttendanceRegistrations = result.data.attendances;
      return this.allAttendanceRegistrations;
    }, err => this.$q.reject(err));
  }

  getGroupsRegisterRecords(id) {
    return this.$http({
      method: 'GET',
      url: `/SCR/student/${id}/groupsRegisterRecords`
    }).then(result => {
      this.groupsRegisterRecords = result.data.groupsRegisterRecords;
      return this.groupsRegisterRecords;
    }, err => this.$q.reject(err));
  }

  addGroupsRegisterRecord(id, groupRegisterRecord) {
    const copyGroupRegisterRecord = {};
    angular.copy(groupRegisterRecord, copyGroupRegisterRecord);
    return this.$http({
      method: 'POST',
      url: `/SCR/student/${id}/groupsRegisterRecords`,
      data: {groupRegisterRecord}
    }).then(result => {
      const index = _indexOf(this.students, _find(this.students, student => student.id === id));
      if (index > -1) {
        this.students[index].groupId = copyGroupRegisterRecord.group.id;
        this.students[index].groupName = copyGroupRegisterRecord.group.name;
      }

      return result;
    }, err => this.$q.reject(err));
  }

  editGroupsRegisterRecord(id, groupRegisterRecord) {
    const copyGroupRegisterRecord = {};
    angular.copy(groupRegisterRecord, copyGroupRegisterRecord);
    return this.$http({
      method: 'PUT',
      url: `/SCR/student/${id}/groupsRegisterRecords/${groupRegisterRecord.id}`,
      data: {groupRegisterRecord}
    }).then(result => {
      const index = _indexOf(this.students, _find(this.students, student => student.id === id));
      if (index > -1) {
        this.students[index].groupId = copyGroupRegisterRecord.group.id;
        this.students[index].groupName = copyGroupRegisterRecord.group.name;
      }

      return result;
    }, err => this.$q.reject(err));
  }

  deleteGroupsRegisterRecord(studentId, id) {
    return this.$http({
      method: 'DELETE',
      url: `/SCR/student/${studentId}/groupsRegisterRecords/${id}`
    }).then(() => {
      if (this.groupsRegisterRecords.length > 0) {
        _remove(this.groupsRegisterRecords, item => item.id === id);
      }
    }, err => this.$q.reject(err));
  }

  getInfo(id) {
    return this.$http({
      method: 'GET',
      url: `/SCR/student/${id}/info`
    }).then(result => result.data, err => this.$q.reject(err));
  }

  edit(params) {
    const {id} = params;
    return this.$http({
      method: 'PUT',
      url: `/SCR/student/${id}`,
      data: params
    }).then(() => params, err => this.$q.reject(err));
  }
}
